import { WORKSPACE_LIMITS } from "../../../constant/workspaceLimits";
import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import ScratchShimmerLoader from "./ScratchShimmerLoader";
import UnifiedConvertCodePanel from "./UnifiedConvertCodePanel";
import { transpileCode, transpileScratchMultiFile } from "../../../utils/transpiler/codeTranspiler";
import { syncPythonToScratchProject } from "../../../utils/transpiler/pythonToScratchSync";
import { validateSyntax } from "../../../utils/transpiler/syntaxValidator";
import { logScratchIncident } from "../../../utils/telemetry/scratchLogger";

const ScratchWorkspace = ({
  mode = "free_play", // "free_play" | "daily_practice" | "competition"
  initialData = null,
  selectedChild = null,
  readOnly = false,
  showScratchTopBar = false, // Configurable flag: false hides default Scratch top menu bar
  showBackpack = false, // Configurable flag: false hides default Scratch bottom backpack panel
  onCodeChange = null,
  onGradeSubmit = null,
  onSaveSnippet = null,
  onSaveSprite = null,
}) => {
  const [isIframeReady, setIsIframeReady] = useState(false);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [targetLang, setTargetLang] = useState("python");
  const [latestProjectData, setLatestProjectData] = useState(null);
  const [showConvertPanel, setShowConvertPanel] = useState(false);
  const scratchContainerRef = useRef(null);
  const hasLoadedInitialData = useRef(false);
  const currentTargetLangRef = useRef("python");
  const currentTargetNameRef = useRef("Sprite1");
  const onSaveSnippetRef = useRef(onSaveSnippet);
  const onSaveSpriteRef = useRef(onSaveSprite);
  const isIframeReadyRef = useRef(false);
  const lastPongTimeRef = useRef(Date.now());
  const lastCodeRef = useRef("");

  useEffect(() => {
    const notifyFullscreenState = () => {
      const iframe = document.querySelector("iframe[title='Practido Visual Block Studio']");
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: "WORKSPACE_FULLSCREEN_STATE",
            payload: { isFullscreen: !!document.fullscreenElement },
          },
          "*"
        );
      }
    };
    document.addEventListener("fullscreenchange", notifyFullscreenState);
    return () => document.removeEventListener("fullscreenchange", notifyFullscreenState);
  }, []);

  useEffect(() => {
    onSaveSnippetRef.current = onSaveSnippet;
  }, [onSaveSnippet]);

  useEffect(() => {
    onSaveSpriteRef.current = onSaveSprite;
  }, [onSaveSprite]);

  useEffect(() => {
    if (initialData) {
      hasLoadedInitialData.current = false; // Reset when new data comes in
    }
  }, [initialData]);

  // Sync Scratch top bar & backpack visibility state with iframe
  useEffect(() => {
    const iframe = document.querySelector("iframe[title='Practido Visual Block Studio']");
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        { type: "SET_SHOW_SCRATCH_TOP_BAR", payload: { show: showScratchTopBar } },
        "*"
      );
      iframe.contentWindow.postMessage(
        { type: "SET_SHOW_BACKPACK", payload: { show: showBackpack } },
        "*"
      );
    }
  }, [showScratchTopBar, showBackpack, isIframeLoaded, isIframeReady]);

  const latestProjectDataRef = useRef(latestProjectData);
  useEffect(() => {
    latestProjectDataRef.current = latestProjectData;
  }, [latestProjectData]);

  const onCodeChangeRef = useRef(onCodeChange);
  useEffect(() => {
    onCodeChangeRef.current = onCodeChange;
  }, [onCodeChange]);

  const getLiveProjectData = () => {
    try {
      const iframe = document.querySelector("iframe[title='Practido Visual Block Studio']");
      if (iframe && iframe.contentWindow && iframe.contentWindow.vm && typeof iframe.contentWindow.vm.toJSON === 'function') {
        const liveJsonStr = iframe.contentWindow.vm.toJSON();
        const liveJson = typeof liveJsonStr === "string" ? JSON.parse(liveJsonStr) : liveJsonStr;
        if (liveJson && liveJson.targets) {
          latestProjectDataRef.current = liveJson;
          setLatestProjectData(liveJson);
          return liveJson;
        }
      }
    } catch (e) {
      // Cross-origin fallback or error
    }
    return latestProjectDataRef.current;
  };

  const sendTranspiledCodeToIframe = (data, lang, targetName = null) => {
    const iframe = document.querySelector("iframe[title='Practido Visual Block Studio']");
    if (!iframe || !iframe.contentWindow) return;
    const activeLang = lang || currentTargetLangRef.current || "python";
    const liveData = getLiveProjectData();
    const projectData = data || liveData || latestProjectDataRef.current || null;
    const activeTarget = targetName || currentTargetNameRef.current;

    const multiFile = transpileScratchMultiFile({
      targetLang: activeLang,
      sourceCode: projectData,
    });

    const singleCode = transpileCode({
      sourceEnv: "scratch",
      targetLang: activeLang,
      sourceCode: projectData,
      targetName: activeTarget || multiFile.activeTarget,
    });

    iframe.contentWindow.postMessage(
      {
        type: "RENDER_CONVERTED_CODE_PANEL",
        payload: {
          code: singleCode,
          targetLang: activeLang,
          multiFile: multiFile,
          activeTarget: activeTarget || multiFile.activeTarget,
        },
      },
      "*"
    );
  };

  useEffect(() => {
    // Fire initial environment registration
    if (onCodeChangeRef.current) {
      onCodeChangeRef.current(null, "scratch");
    }

    const handleMessage = (e) => {
      if (!e.data) return;

      if (e.data.type === "CONVERT_CODE_TAB_CLICKED") {
        setShowConvertPanel(true);
        const incomingJson = e.data.payload?.projectJson;
        if (incomingJson) {
          latestProjectDataRef.current = incomingJson;
          setLatestProjectData(incomingJson);
        }
        const liveData = getLiveProjectData();
        const lang = e.data.payload?.targetLang || currentTargetLangRef.current || "python";
        const targetName = e.data.payload?.targetName || currentTargetNameRef.current || "Sprite1";
        currentTargetNameRef.current = targetName;
        sendTranspiledCodeToIframe(liveData || incomingJson || latestProjectDataRef.current, lang, targetName);
      }

      if (e.data.type === "CHANGE_TARGET_LANG") {
        const newLang = e.data.payload?.targetLang || "python";
        setTargetLang(newLang);
        currentTargetLangRef.current = newLang;
        const liveData = getLiveProjectData();
        sendTranspiledCodeToIframe(liveData || latestProjectDataRef.current, newLang, currentTargetNameRef.current);
      }

      if (e.data.type === "SELECT_CONVERT_FILE") {
        const requestedTarget = e.data.payload?.targetName;
        if (requestedTarget) {
          currentTargetNameRef.current = requestedTarget;
          const liveData = getLiveProjectData();
          sendTranspiledCodeToIframe(liveData || latestProjectDataRef.current, currentTargetLangRef.current, requestedTarget);
        }
      }

      if (e.data.type === "SYNC_CODE_TO_SCRATCH_BLOCKS") {
        const { code, targetLang: codeLang, targetName } = e.data.payload || {};
        const activeCodeLang = codeLang || currentTargetLangRef.current || "python";
        const iframe = document.querySelector("iframe[title='Practido Visual Block Studio']");
        const currentData = getLiveProjectData() || latestProjectDataRef.current;

        if (code !== undefined && code !== null) {
          lastCodeRef.current = code;
          lastPongTimeRef.current = Date.now();
        }

        if (code !== undefined && code !== null && currentData) {
          const validation = validateSyntax(code, activeCodeLang);
          if (!validation.isValid && validation.errors && validation.errors.length > 0) {
            const err = validation.errors[0];
            if (iframe && iframe.contentWindow) {
              iframe.contentWindow.postMessage(
                {
                  type: "SYNC_ERROR",
                  payload: {
                    line: err.line,
                    error: err.message,
                    tip: err.tip,
                  },
                },
                "*"
              );
            }
            return;
          }

          const syncResult = syncPythonToScratchProject(code, currentData, activeCodeLang, targetName || currentTargetNameRef.current);
          if (syncResult.success && syncResult.updatedProject) {
            setLatestProjectData(syncResult.updatedProject);
            latestProjectDataRef.current = syncResult.updatedProject;

            // Telemetry: Log any anomaly detected during sync (e.g. unexpected block loss)
            if (syncResult.anomalies && Array.isArray(syncResult.anomalies)) {
              syncResult.anomalies.forEach((anom) => {
                logScratchIncident(anom.type, {
                  ...anom,
                  codeSnippet: code,
                  activeCodeLang,
                });
              });
            }

            if (iframe && iframe.contentWindow) {
              // Update VM blocks in-place without destroying runtime targets or assets
              iframe.contentWindow.postMessage(
                {
                  type: "SYNC_BLOCKS_TO_VM",
                  payload: {
                    targetName: targetName || currentTargetNameRef.current,
                    targets: syncResult.updatedProject.targets,
                    projectJson: syncResult.updatedProject,
                  },
                },
                "*"
              );
              iframe.contentWindow.postMessage(
                {
                  type: "SYNC_COMPLETED",
                  payload: {
                    mutationsCount: syncResult.mutationsCount,
                    mutationLogs: syncResult.mutationLogs,
                    hasWarnings: syncResult.hasWarnings,
                    unparsedLines: syncResult.unparsedLines,
                  },
                },
                "*"
              );
            }
            if (onCodeChangeRef.current) {
              onCodeChangeRef.current(syncResult.updatedProject, "scratch");
            }
          } else {
            // Telemetry: Log unexpected sync failure/fallback
            logScratchIncident("SYNC_FALLBACK_TRIGGERED", {
              severity: "error",
              message: syncResult.error || "Failed to synchronize code to Scratch blocks",
              tip: syncResult.tip || null,
              line: syncResult.line || 1,
              codeSnippet: code,
              targetName: targetName || currentTargetNameRef.current,
              activeCodeLang,
            });

            if (iframe && iframe.contentWindow) {
              iframe.contentWindow.postMessage(
                {
                  type: "SYNC_ERROR",
                  payload: {
                    line: syncResult.line || 1,
                    error: syncResult.error || "Failed to synchronize code to Scratch blocks",
                    tip: syncResult.tip || "Please check your code syntax and block mappings.",
                  },
                },
                "*"
              );
            }
          }
        }
      }

      if (e.data.type === "SCRATCH_PONG") {
        lastPongTimeRef.current = Date.now();
        if (!isIframeReadyRef.current && e.data.payload?.vmReady) {
          setIsIframeReady(true);
          isIframeReadyRef.current = true;
        }
      }

      if (e.data.type === "SCRATCH_IFRAME_ERROR") {
        const errorPayload = e.data.payload || {};
        logScratchIncident("IFRAME_RUNTIME_CRASH", {
          severity: "critical",
          message: errorPayload.message || "Unhandled Scratch iframe runtime error",
          errorStack: errorPayload.stack || null,
          targetName: currentTargetNameRef.current,
          codeSnippet: lastCodeRef.current || null,
          extra: {
            source: errorPayload.source || null,
            lineno: errorPayload.lineno || null,
            colno: errorPayload.colno || null,
          },
        });
      }

      if (e.data.type === "SEND_PROJECT_JSON" || e.data.type === "SCRATCH_PROJECT_CHANGE") {
        setIsIframeReady(true);
        isIframeReadyRef.current = true;
        const projectData = e.data.payload;
        const projectJson = projectData?.projectJson || projectData;
        if (projectJson) {
          setLatestProjectData(projectJson);
          latestProjectDataRef.current = projectJson;
        }
        // Only trigger onCodeChange if user actively modified or VM returned state
        if (onCodeChangeRef.current && projectData && hasLoadedInitialData.current) {
          onCodeChangeRef.current(projectData, "scratch");
        }
      }

      if (e.data.type === "SEND_STAGE_THUMBNAIL") {
        const thumb = e.data.payload?.thumbnail;
        if (thumb && onCodeChangeRef.current && latestProjectDataRef.current) {
          onCodeChangeRef.current({
            projectJson: latestProjectDataRef.current,
            thumbnail: thumb
          }, "scratch");
        }
      }

      if (e.data.type === "TRIGGER_SAVE_SCRATCH_SNIPPET") {
        if (onSaveSnippetRef.current) {
          onSaveSnippetRef.current({
            snippetJson: e.data.payload?.snippetJson,
            defaultName: e.data.payload?.defaultName,
            environment: "scratch",
          });
        }
      }

      if (e.data.type === "TRIGGER_SAVE_SCRATCH_SPRITE") {
        if (onSaveSpriteRef.current) {
          onSaveSpriteRef.current({
            spriteCode: e.data.payload?.spriteCode,
            thumbnail: e.data.payload?.thumbnail,
            defaultName: e.data.payload?.defaultName,
          });
        }
      }

      if (e.data.type === "TOGGLE_WORKSPACE_FULLSCREEN") {
        const el = scratchContainerRef.current || document.documentElement;
        if (!document.fullscreenElement) {
          el.requestFullscreen().catch((err) => {
            console.warn("Failed to enter fullscreen mode:", err);
          });
        } else if (document.exitFullscreen) {
          document.exitFullscreen();
        }
      }

      if (e.data.type === "SNIPPET_INSERTED_SUCCESS") {
        toast.success(
          `Blocks from "${e.data.payload?.name || "Snippet"}" added to workspace!`
        );
      }

      if (e.data.type === "SNIPPET_INSERT_ERROR") {
        toast.error(
          e.data.error || "Failed to insert snippet blocks into workspace."
        );
      }

      if (e.data.type === "SPRITE_ADDED_SUCCESS") {
        toast.success(
          `Sprite "${e.data.payload?.name || "Sprite"}" added to project!`
        );
      }

      if (e.data.type === "LOAD_ERROR") {
        toast.error(`Failed to load Scratch project: ${e.data.error || "Corrupted project file"}`);
      }

      if (e.data.type === "ASSET_SIZE_LIMIT_EXCEEDED") {
        const { assetType, assetName, incomingSizeBytes } = e.data.payload || {};
        const label = assetName ? `"${assetName}"` : (assetType ? assetType.toLowerCase() : "asset");
        const formattedIncoming = incomingSizeBytes ? ` (${(incomingSizeBytes / 1024).toFixed(1)} KB)` : "";
        toast.warning(
          `Project limit (${e.data.payload && e.data.payload.maxLimitBytes ? (e.data.payload.maxLimitBytes >= 1024 * 1024 ? (e.data.payload.maxLimitBytes / (1024 * 1024)).toFixed(0) + " MB" : Math.round(e.data.payload.maxLimitBytes / 1024) + " KB") : "500 KB"}) reached! Adding ${label}${formattedIncoming} would exceed the allowed project size. Please choose a smaller file or delete unused sprites/costumes.`,
          { autoClose: 6000 }
        );
      }
    };

    window.addEventListener("message", handleMessage);

    // Request full save package on demand when save is triggered
    const handleSavePayloadRequest = () => {
      const iframe = document.querySelector("iframe[title='Practido Visual Block Studio']");
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({ type: "GET_PROJECT_JSON", payload: { includeSb3: true } }, "*");
      }
    };
    window.addEventListener("workspace-request-save-payload", handleSavePayloadRequest);

    // Periodically send heartbeat ping to Scratch VM iframe (every 3000ms) without heavy .sb3 re-zipping
    const interval = setInterval(() => {
      const iframe = document.querySelector("iframe[title='Practido Visual Block Studio']");
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({ type: "SCRATCH_PING" }, "*");

        // Heartbeat timeout check: If VM was previously ready and hasn't responded to pings in > 15s
        if (isIframeReadyRef.current && Date.now() - lastPongTimeRef.current > 15000) {
          logScratchIncident("SCRATCH_HANG_TIMEOUT", {
            severity: "critical",
            message: "Scratch VM iframe unresponsive (heartbeat timeout > 15s)",
            targetName: currentTargetNameRef.current,
            codeSnippet: lastCodeRef.current || null,
          });
        }
      }
    }, 3000);

    // Safety fallback timer for initial load shimmer transition
    const safetyTimer = setTimeout(() => {
      setIsIframeLoaded(true);
    }, 4500);

    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("workspace-request-save-payload", handleSavePayloadRequest);
      clearInterval(interval);
      clearTimeout(safetyTimer);
    };
  }, []);

  // Load project initialData into Scratch VM iframe as soon as iframe reports readiness
  useEffect(() => {
    if (initialData && isIframeReady && !hasLoadedInitialData.current) {
      const iframe = document.querySelector("iframe[title='Practido Visual Block Studio']");
      if (iframe && iframe.contentWindow) {
        if (initialData instanceof ArrayBuffer) {
          iframe.contentWindow.postMessage(
            { type: "LOAD_PROJECT_BUFFER", payload: { buffer: initialData } },
            "*"
          );
        } else {
          let payloadToLoad = initialData;
          if (initialData && typeof initialData === "object" && !(initialData instanceof ArrayBuffer)) {
            if (Array.isArray(initialData.files) && initialData.files.length > 0) {
              const entryFile = initialData.files.find((f) => f.isEntry) || initialData.files[0];
              payloadToLoad = entryFile?.content || initialData.code;
            } else if (initialData.code !== undefined && initialData.code !== null) {
              payloadToLoad = initialData.code;
            }
          }

          if (typeof payloadToLoad === "string") {
            try {
              payloadToLoad = JSON.parse(payloadToLoad);
            } catch (e) {}
          }

          iframe.contentWindow.postMessage(
            { type: "LOAD_PROJECT_JSON", payload: payloadToLoad },
            "*"
          );
        }
        hasLoadedInitialData.current = true;
      }
    }
  }, [initialData, isIframeReady]);

  // If initialData is null (e.g. New Project), mark as loaded so user edits are captured
  useEffect(() => {
    if (!initialData && isIframeReady) {
      hasLoadedInitialData.current = true;
    }
  }, [initialData, isIframeReady]);

  const isWorkspaceFullyLoaded = isIframeReady || isIframeLoaded;

  return (
    <div ref={scratchContainerRef} className="w-full h-full bg-slate-900 overflow-hidden relative flex flex-col min-h-[640px]">
      <ScratchShimmerLoader
        title="Practido Visual Block Studio"
        subTitle="Preparing your interactive visual workspace"
        isLoaded={isWorkspaceFullyLoaded}
      />

      <iframe
        title="Practido Visual Block Studio"
        src={`/scratch/index.html?showScratchTopBar=${showScratchTopBar}&showBackpack=${showBackpack}&maxProjectLimitBytes=${WORKSPACE_LIMITS.WORKSPACE_MEMORY_LIMIT_BYTES}`}
        loading="eager"
        onLoad={() => {
          setIsIframeLoaded(true);
          const iframe = document.querySelector("iframe[title='Practido Visual Block Studio']");
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage(
              { type: "SET_SHOW_SCRATCH_TOP_BAR", payload: { show: showScratchTopBar } },
              "*"
            );
            iframe.contentWindow.postMessage(
              { type: "SET_SHOW_BACKPACK", payload: { show: showBackpack } },
              "*"
            );
          }
        }}
        className="w-full h-full border-0 block flex-1 min-h-[640px]"
        allow="fullscreen; autoplay"
      />
    </div>
  );
};

export default ScratchWorkspace;


